import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import {
  bqnsNamed,
  parseClusterStatusResponse,
  parseHostname,
  parseShowClusterBqn,
  usersSyncCommands,
  withHostname,
} from "./api";
import { asNodes, nodesNamed, rememberHostnames } from "./hostnames";
import { withEntryBqnId } from "./entryNode";

const BqnContext = createContext({
  bqnId: undefined,
  loginBqnId: undefined,
  clusterStatus: null,
  setBqnId: () => {},
  bqns: undefined,
  onlineBqns: [],
  loadClusterStatus: () => {},
  loadHostnames: () => {},
  nameBqns: () => {},
  loadOnce: () => {},
});

const doUsersSync = (command) => {
  ifCl.run(command).catch(console.error);
};

export function useBqnId() {
  const bqnContext = useContext(BqnContext);
  return bqnContext.bqnId;
}

export function useBqns() {
  const bqnContext = useContext(BqnContext);
  return bqnContext.bqns;
}

export function usePrimaryBqn() {
  const { bqns } = useContext(BqnContext);

  if (bqns === undefined) {
    return undefined;
  }

  const primaryBqn = bqns.find(
    ({ function: functionName }) => functionName === "primary",
  );
  if (primaryBqn && primaryBqn.bqnId) {
    return primaryBqn.bqnId;
  }
  return undefined;
}

export function useClusterStatus() {
  const bqnContext = useContext(BqnContext);
  return bqnContext.clusterStatus;
}

const forwardAfter = doMethod => value => {
  doMethod(value);
  return value;
}

// Only "show system hostname" is asked of the BQN of the session, whose ID
// the CLI client retrieved already.
const clusterBatch = (bqnIdSession) =>
  [
    "show cluster bqn",
    withEntryBqnId("show system hostname", bqnIdSession),
    "show cluster status",
    "",
  ].join("\n");

// The BQNs of the cluster, their function and their hostname, to follow the
// names they get.
const bqnsNamesKey = (bqns) =>
  (bqns ?? [])
    .map(({bqnId, function: _function, hostname}) =>
      `${bqnId}:${_function}:${hostname ?? ""}`)
    .join(",");

export const BqnContextProvider = ({ children }) => {
  const [bqnId, setBqnId] = useState();
  const [clusterStatus, setClusterStatus] = useState();
  const [bqns, setBqns] = useState();
  const [onlineBqns, setOnlineBqns] = useState([]);
  const [loginBqnId, setLoginBqnId] = useState();
  const bqnIdRef = useRef();
  // The request of the cluster on its way, if there is one.
  const loadingRef = useRef(null);
  // The BQNs whose names were seen already, to sync the ones that get one.
  const bqnsSyncedRef = useRef();
  const mode = clusterStatus?.mode;
  bqnIdRef.current = bqnId;

  useEffect(() => {
    if (bqnId === undefined || loginBqnId === undefined) {
      return;
    }

    window.bqnId = bqnId; // For usage from the JS only. Use context from React.
    window.globalSetBqnId = innerSetBqnId;
  }, [bqnId, loginBqnId]);

  useEffect(() => {
    if (bqns) {
      setOnlineBqns(
        bqns.filter(({ function: _function }) => _function !== "offline"),
      );
    }
  }, [JSON.stringify(bqns)]);

  // The names of the BQNs are asked for only when the list of the selector is
  // opened (loadHostnames), which is the only place where they are read: a BQN
  // listed as online but disconnected does not answer its name, and asking it
  // for one holds every command of the GUI behind it. The cluster dashboard
  // asks for all of them at once for its own cards, and hands them over
  // (nameBqns), so the selector lists them once the dashboard has been seen.
  //
  // The users are pushed into a BQN that gets a hostname without having one:
  // it has just come online, so it takes the users of the primary. Nothing
  // else asks for a sync, neither the BQN selected in the header nor a change
  // of function of any BQN.
  useEffect(() => {
    usersSyncCommands(mode, bqnsSyncedRef.current, bqns).forEach(doUsersSync);
    bqnsSyncedRef.current = bqns;
  }, [mode, bqnsNamesKey(bqns)]);

  function innerSetBqnId(bqnId) {
    setBqnId(bqnId);
  }

  const loadClusterStatus = () =>
    ifCl.getEntryBqnId().then((bqnIdSession) =>
      ifCl
        .run(clusterBatch(bqnIdSession), "batch_exmode")
        .then((response) => {
          const [bqnsRes, hostnameRes, statusRes] =
            response.split(batchExModeSeparator);
          const clusterStatusRes = parseClusterStatusResponse(statusRes);
          const currentBqnId = bqnIdSession ?? Number(clusterStatusRes.bqnId);
          const clusterBqnRes = asNodes(
            withHostname(
              parseShowClusterBqn(bqnsRes),
              parseHostname(hostnameRes),
              currentBqnId,
            ),
          );
          setBqns(clusterBqnRes);
          setLoginBqnId(currentBqnId);

          setClusterStatus(clusterStatusRes);

          let defaultBqnId = bqnIdRef.current; // Use ref instead of stale closure variable

          if (defaultBqnId === undefined) {
            if (window.location.pathname === "/viewClusterDashboard") {
              defaultBqnId = "all";
            } else if (clusterStatusRes.mode === "standalone") {
              defaultBqnId = Number(clusterStatusRes.bqnId);
            } else {
              const node = clusterBqnRes.find(
                (bqn) => Number(bqn.bqnId) === Number(currentBqnId),
              );
              defaultBqnId = node
                ? node.bqnId
                : (clusterBqnRes[0]?.bqnId ?? "all");
            }
          }

          setBqnId(defaultBqnId);

          return { bqnId: defaultBqnId, bqns: clusterBqnRes };
        }),
    );

  const requestClusterBqn = () =>
    ifCl
      .run("show cluster bqn")
      .then(parseShowClusterBqn)
      .then(asNodes);

  const loadClusterBqn = () =>
    requestClusterBqn()
      .then(forwardAfter(setBqns));

  const loadHostnames = () =>
    requestClusterBqn()
      .then(nodesNamed)
      .then(forwardAfter(setBqns));

  // The BQNs named by the answer to "show system hostname detail" a view got
  // for itself, which is kept for the times the cluster is read again. An
  // answer that names nobody, empty or made of errors, is not taken: it would
  // take every BQN as offline.
  const nameBqns = (hostnames) => {
    const hostnamesReported = rememberHostnames(hostnames);

    if (Object.keys(hostnamesReported).length === 0) {
      return;
    }
    setBqns((bqnsCurrent) =>
      bqnsCurrent === undefined
        ? bqnsCurrent
        : bqnsNamed(bqnsCurrent, hostnamesReported),
    );
  };

  // The cluster is loaded once, for the first of the readers that asks for
  // it: the routes ask from more than one effect of the same render, and the
  // state above is only what the answer of a request that came back left, so
  // the request on its way is what keeps the commands from being run again.
  //
  // A request that failed leaves neither, so the next one to ask runs them.
  function loadOnce() {
    if (loadingRef.current !== null) {
      return loadingRef.current;
    }
    if (bqnId !== undefined || bqns !== undefined) {
      return Promise.resolve();
    }

    loadingRef.current = loadClusterStatus().finally(() => {
      loadingRef.current = null;
    });

    return loadingRef.current;
  }

  return (
    <BqnContext.Provider
      value={{
        setBqnId: innerSetBqnId,
        bqnId,
        clusterStatus,
        loadOnce,
        bqns,
        loadClusterStatus,
        loadClusterBqn,
        loadHostnames,
        nameBqns,
        loginBqnId,
        onlineBqns,
      }}
    >
      {children}
    </BqnContext.Provider>
  );
};

export default BqnContext;
